import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SERVICES, BRAND } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayPlus(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}

export default function Booking() {
  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative py-24 sm:py-32 bg-slate-50"
    >
      {/* Subtle decorative blob */}
      <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-blue-100/60 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left intro */}
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
            Book or Contact
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
            Pick a time.
            <br />
            We'll handle the rest.
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Reserve a slot for a guaranteed, in-and-out visit — or send us a
            message and we'll get back to you the same business day.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 grid place-items-center shrink-0">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Call us
                </div>
                <a
                  href={BRAND.phoneHref}
                  data-testid="booking-phone-link"
                  className="font-display text-xl font-semibold text-slate-900 hover:text-blue-600"
                >
                  {BRAND.phone}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 grid place-items-center shrink-0">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Email
                </div>
                <a
                  href={BRAND.emailHref}
                  data-testid="booking-email-link"
                  className="font-display text-base font-semibold text-slate-900 hover:text-blue-600"
                >
                  {BRAND.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 grid place-items-center shrink-0">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Visit
                </div>
                <div className="font-display text-base font-semibold text-slate-900">
                  {BRAND.address}
                </div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 grid place-items-center shrink-0">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div className="space-y-0.5">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Hours
                </div>
                {BRAND.hours.map((h) => (
                  <div key={h.day} className="text-sm text-slate-700">
                    <span className="font-semibold">{h.day}</span> — {h.time}
                  </div>
                ))}
              </div>
            </li>
          </ul>
        </div>

        {/* Right: tabs (Appointment / Contact) */}
        <div className="lg:col-span-7">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6 sm:p-8">
            <Tabs defaultValue="appointment" className="w-full">
              <TabsList
                data-testid="booking-tabs"
                className="grid w-full grid-cols-2 h-12 rounded-xl bg-slate-100 p-1"
              >
                <TabsTrigger
                  value="appointment"
                  data-testid="tab-appointment"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                >
                  Book Appointment
                </TabsTrigger>
                <TabsTrigger
                  value="contact"
                  data-testid="tab-contact"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                >
                  Send a Message
                </TabsTrigger>
              </TabsList>
              <TabsContent value="appointment" className="mt-7">
                <AppointmentForm />
              </TabsContent>
              <TabsContent value="contact" className="mt-7">
                <LeadForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
}

// ====================== APPOINTMENT FORM ======================
function AppointmentForm() {
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [closed, setClosed] = useState(false);
  const [closedReason, setClosedReason] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Fetch availability when date changes
  useEffect(() => {
    if (!date) return;
    const ds = format(date, "yyyy-MM-dd");
    setLoadingSlots(true);
    setTime("");
    axios
      .get(`${API}/appointments/availability`, { params: { date: ds } })
      .then((res) => {
        setSlots(res.data.slots || []);
        setClosed(!!res.data.closed);
        setClosedReason(res.data.reason || "");
      })
      .catch(() => {
        setSlots([]);
        setClosed(false);
        toast.error("Could not load available time slots. Please try again.");
      })
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const disabledDays = useMemo(
    () => [{ before: todayPlus(0) }, { dayOfWeek: [0] }], // past + Sundays
    []
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time || !service || !name || !email || !phone) {
      toast.error("Please fill in every required field.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments`, {
        name,
        email,
        phone,
        service,
        date: format(date, "yyyy-MM-dd"),
        time,
        notes,
      });
      setSuccess(res.data);
      toast.success("Appointment confirmed! We'll see you soon.");
      // reset
      setName("");
      setEmail("");
      setPhone("");
      setService("");
      setNotes("");
      setTime("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not book your appointment. Please try again or call us."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="appointment-success"
        className="text-center py-10 px-4 animate-fade-up"
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">
          You're all set!
        </h3>
        <p className="mt-2 text-slate-600">
          We've reserved <b>{success.time}</b> on{" "}
          <b>{format(new Date(success.date), "EEEE, MMM d")}</b> for{" "}
          <b>{success.service_label}</b>.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          A confirmation will be sent to {success.email}. Need to change it?
          Call us at {BRAND.phone}.
        </p>
        <Button
          data-testid="appointment-new-button"
          onClick={() => setSuccess(null)}
          className="mt-7 bg-slate-900 hover:bg-slate-800 text-white rounded-lg h-11 px-6"
        >
          Book another appointment
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="appointment-form"
      className="space-y-6"
    >
      {/* Service */}
      <div>
        <Label className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700">
          Service
        </Label>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger
            data-testid="appointment-service-select"
            className="mt-2 h-12 rounded-lg border-slate-300 bg-white"
          >
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {SERVICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
            <SelectItem value="other">Other / Not Sure</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Date + Time */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700">
            Preferred date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="appointment-date-trigger"
                className={`mt-2 w-full h-12 justify-start rounded-lg border-slate-300 bg-white font-medium ${
                  !date ? "text-slate-500" : "text-slate-900"
                }`}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-blue-600" />
                {date ? format(date, "EEEE, MMM d, yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={disabledDays}
                fromDate={todayPlus(0)}
                toDate={todayPlus(60)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700">
            Time slot
          </Label>
          <div className="mt-2 min-h-[3rem]">
            {!date && (
              <div className="h-12 rounded-lg border border-dashed border-slate-300 grid place-items-center text-sm text-slate-500">
                Pick a date first
              </div>
            )}
            {date && loadingSlots && (
              <div className="h-12 rounded-lg border border-slate-200 grid place-items-center text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {date && !loadingSlots && closed && (
              <div className="h-12 rounded-lg border border-amber-200 bg-amber-50 grid place-items-center text-sm text-amber-700 font-medium">
                {closedReason || "Closed on this date"}
              </div>
            )}
            {date && !loadingSlots && !closed && (
              <div
                data-testid="appointment-slot-grid"
                className="grid grid-cols-3 gap-2 max-h-44 overflow-auto pr-1"
              >
                {slots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    disabled={!slot.available}
                    data-testid={`slot-${slot.time.replace(/[^a-zA-Z0-9]/g, "")}`}
                    onClick={() => setTime(slot.time)}
                    className={`text-xs sm:text-sm font-semibold rounded-lg h-10 transition-all ${
                      time === slot.time
                        ? "bg-blue-600 text-white shadow-sm"
                        : slot.available
                          ? "bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700"
                          : "bg-slate-100 text-slate-400 line-through cursor-not-allowed"
                    }`}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact info */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          id="appt-name"
          label="Full name"
          value={name}
          onChange={setName}
          placeholder="Jane Doe"
          testId="appointment-name"
          required
        />
        <Field
          id="appt-phone"
          label="Phone"
          value={phone}
          onChange={setPhone}
          placeholder="(619) 555-0142"
          testId="appointment-phone"
          required
        />
      </div>
      <Field
        id="appt-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testId="appointment-email"
        required
      />

      <div>
        <Label
          htmlFor="appt-notes"
          className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700"
        >
          Anything we should know?
        </Label>
        <Textarea
          id="appt-notes"
          data-testid="appointment-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Vehicle details, special situation, etc. (optional)"
          className="mt-2 min-h-[96px] rounded-lg border-slate-300 bg-white"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="appointment-submit"
        className="w-full h-13 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-base font-semibold shadow-md shadow-blue-600/20"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Reserving your slot…
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5 mr-2" />
            Confirm Appointment
          </>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        By booking you agree we may contact you about your appointment. No spam, ever.
      </p>
    </form>
  );
}

// ====================== CONTACT / LEAD FORM ======================
function LeadForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone || !message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name,
        email,
        phone,
        service: service || null,
        message,
      });
      setSuccess(true);
      toast.success("Thanks! We'll be in touch shortly.");
      setName("");
      setEmail("");
      setPhone("");
      setService("");
      setMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not send your message. Please call us instead."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="lead-success" className="text-center py-10">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">
          Message received.
        </h3>
        <p className="mt-2 text-slate-600">
          A team member will get back to you the same business day.
        </p>
        <Button
          onClick={() => setSuccess(false)}
          data-testid="lead-new-button"
          className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-lg h-11 px-6"
        >
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lead-form"
      className="space-y-6"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          id="lead-name"
          label="Full name"
          value={name}
          onChange={setName}
          placeholder="Jane Doe"
          testId="lead-name"
          required
        />
        <Field
          id="lead-phone"
          label="Phone"
          value={phone}
          onChange={setPhone}
          placeholder="(619) 555-0142"
          testId="lead-phone"
          required
        />
      </div>
      <Field
        id="lead-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testId="lead-email"
        required
      />
      <div>
        <Label className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700">
          What do you need help with? (optional)
        </Label>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger
            data-testid="lead-service-select"
            className="mt-2 h-12 rounded-lg border-slate-300 bg-white"
          >
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {SERVICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
            <SelectItem value="other">Other / Not Sure</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label
          htmlFor="lead-message"
          className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700"
        >
          Message
        </Label>
        <Textarea
          id="lead-message"
          data-testid="lead-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us about your vehicle or what you'd like help with…"
          className="mt-2 min-h-[120px] rounded-lg border-slate-300 bg-white"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        data-testid="lead-submit"
        className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-base font-semibold"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Sending…
          </>
        ) : (
          "Send Message"
        )}
      </Button>
    </form>
  );
}

function Field({ id, label, value, onChange, placeholder, type = "text", testId, required }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.16em] font-bold text-slate-700"
      >
        {label}
        {required && <span className="text-blue-600 ml-1">*</span>}
      </Label>
      <Input
        id={id}
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-2 h-12 rounded-lg border-slate-300 bg-white focus-visible:ring-blue-600"
      />
    </div>
  );
}
